import { Cached, ServiceAuthType } from '@uploadcam/common';
import { environment } from '../../environments/environment';
import { LoginWithPopupGenericAdapterInteractor } from '../auth/domain/interactors/login-with-popup-generic-adapter.interactor';
import { RefreshCredentialAdapterInteractor } from '../auth/domain/interactors/refresh-credential.interactor';
import { CommonProvider } from '../common/common.provider';
import { ExchangeMicrosoftAuthCodeInteractor } from './domain/exchange-microsoft-auth-code.interactor';
import { GetMicrosoftAuthUrlInteractor } from './domain/get-microsoft-auth-url.interactor';
import { RefreshMicrosoftCredentialInteractor } from './domain/refresh-microsoft-credential.interactor';
import { LoginWithRedirectAdapterInteractor } from '../auth/domain/interactors/login-with-redirect.interactor';
import { ExchangeAuthCodeInteractor } from '../auth/domain/interactors/exchange-auth-code.interactor';
import { LoginWithRedirectGenericAdapterInteractor } from '../auth/domain/interactors/login-with-redirect-generic-adapter.interactor';
import { LoginWithPopupAdapterInteractor } from '../auth/domain/interactors/login-with-popup-adapter.interactor';
import { CheckLoginPostConditionsInteractor } from '../auth/domain/interactors/check-login-post-conditions.interactor';
import { GetTeamByIdInteractor } from '../team/domain/interactors/get-team-by-id.interactor';
import { GetUserInteractor } from '../auth/domain/interactors/get-user.interactor';
import { CheckLoginPostConditionsMicrosoftInteractor } from './domain/check-login-post-conditions.interactor';
// import { CheckLoginPostConditionsMicrosoftInteractor } from './domain/check-login-post-conditions.interactor';

type MicrosoftAuthType =
  | ServiceAuthType.MicrosoftPersonalFull
  | ServiceAuthType.MicrosoftTeamFull
  | ServiceAuthType.MicrosoftTeamSites;
type MicrosoftAuthTypeMap<T> = Record<MicrosoftAuthType, T>;

export abstract class AuthMicrosoftProvider {
  abstract provideCheckLoginPostConditions(
    getTeamById: GetTeamByIdInteractor,
    getUser: GetUserInteractor,
  ): CheckLoginPostConditionsInteractor;
  abstract provideExchangeMicrosoftPersonalFullAuthCode(): ExchangeAuthCodeInteractor;
  abstract provideExchangeMicrosoftTeamFullAuthCode(): ExchangeAuthCodeInteractor;
  abstract provideExchangeMicrosoftTeamSitesAuthCode(): ExchangeAuthCodeInteractor;
  abstract provideLoginWithPopupMicrosoftPersonalFullAdapter(): LoginWithPopupAdapterInteractor;
  abstract provideLoginWithPopupMicrosoftTeamFullAdapter(): LoginWithPopupAdapterInteractor;
  abstract provideLoginWithPopupMicrosoftTeamSitesAdapter(): LoginWithPopupAdapterInteractor;
  abstract provideLoginWithRedirectMicrosoftPersonalFullAdapter(): LoginWithRedirectAdapterInteractor;
  abstract provideLoginWithRedirectMicrosoftTeamFullAdapter(): LoginWithRedirectAdapterInteractor;
  abstract provideLoginWithRedirectMicrosoftTeamSitesAdapter(): LoginWithRedirectAdapterInteractor;
  abstract provideRefreshMicrosoftCredential(): RefreshCredentialAdapterInteractor;
  abstract provideRequiredScopesPersonalFull(): readonly string[];
  abstract provideRequiredScopesTeamFull(): readonly string[];
  abstract provideRequiredScopesTeamSites(): readonly string[];
}

export class AuthMicrosoftDefaultProvider implements AuthMicrosoftProvider {
  private readonly scopes: MicrosoftAuthTypeMap<string[]> = {
    [ServiceAuthType.MicrosoftPersonalFull]: ['user.read', 'files.readwrite'],
    [ServiceAuthType.MicrosoftTeamFull]: ['user.read', 'files.readwrite', 'sites.read.all'],
    [ServiceAuthType.MicrosoftTeamSites]: ['user.read', 'sites.selected'],
  };

  private readonly getMicrosoftAuthUrlInteractor: MicrosoftAuthTypeMap<GetMicrosoftAuthUrlInteractor> =
    {
      [ServiceAuthType.MicrosoftPersonalFull]: new GetMicrosoftAuthUrlInteractor(
        environment.microsoft.clientId,
        this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftPersonalFull),
        this.scopes[ServiceAuthType.MicrosoftPersonalFull],
      ),
      [ServiceAuthType.MicrosoftTeamFull]: new GetMicrosoftAuthUrlInteractor(
        environment.microsoft.clientId,
        this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftTeamFull),
        this.scopes[ServiceAuthType.MicrosoftTeamFull],
      ),
      [ServiceAuthType.MicrosoftTeamSites]: new GetMicrosoftAuthUrlInteractor(
        environment.microsoft.clientId,
        this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftTeamSites),
        this.scopes[ServiceAuthType.MicrosoftTeamSites],
      ),
    };

  constructor(
    private readonly common: CommonProvider,
    private readonly window: Window,
  ) {}

  public provideCheckLoginPostConditions(
    getTeamById: GetTeamByIdInteractor,
    getUser: GetUserInteractor,
  ): CheckLoginPostConditionsInteractor {
    return new CheckLoginPostConditionsMicrosoftInteractor(getTeamById, getUser);
  }

  @Cached()
  public provideExchangeMicrosoftPersonalFullAuthCode(): ExchangeAuthCodeInteractor {
    return new ExchangeMicrosoftAuthCodeInteractor(
      environment.microsoft.clientId,
      this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftPersonalFull),
      this.scopes[ServiceAuthType.MicrosoftPersonalFull],
    );
  }

  @Cached()
  public provideExchangeMicrosoftTeamFullAuthCode(): ExchangeAuthCodeInteractor {
    return new ExchangeMicrosoftAuthCodeInteractor(
      environment.microsoft.clientId,
      this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftTeamFull),
      this.scopes[ServiceAuthType.MicrosoftTeamFull],
    );
  }

  @Cached()
  public provideExchangeMicrosoftTeamSitesAuthCode(): ExchangeAuthCodeInteractor {
    return new ExchangeMicrosoftAuthCodeInteractor(
      environment.microsoft.clientId,
      this.common.provideOAuthRedirectUri(ServiceAuthType.MicrosoftTeamSites),
      this.scopes[ServiceAuthType.MicrosoftTeamSites],
    );
  }

  @Cached()
  public provideLoginWithPopupMicrosoftPersonalFullAdapter(): LoginWithPopupAdapterInteractor {
    return new LoginWithPopupGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftPersonalFull],
      this.common.provideLogger().withTag('LoginWithPopupInteractor<MicrosoftPersonalFull>'),
      environment.auth.popup.timeout,
      this.window,
      'Microsoft Login',
    );
  }

  @Cached()
  public provideLoginWithPopupMicrosoftTeamFullAdapter(): LoginWithPopupAdapterInteractor {
    return new LoginWithPopupGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftTeamFull],
      this.common.provideLogger().withTag('LoginWithPopupInteractor<MicrosoftTeamFull>'),
      environment.auth.popup.timeout,
      this.window,
      'Microsoft Login',
    );
  }

  @Cached()
  public provideLoginWithPopupMicrosoftTeamSitesAdapter(): LoginWithPopupAdapterInteractor {
    return new LoginWithPopupGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftTeamSites],
      this.common.provideLogger().withTag('LoginWithPopupInteractor<MicrosoftTeamSites>'),
      environment.auth.popup.timeout,
      this.window,
      'Microsoft Login',
    );
  }

  @Cached()
  public provideLoginWithRedirectMicrosoftPersonalFullAdapter(): LoginWithRedirectAdapterInteractor {
    return new LoginWithRedirectGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftPersonalFull],
    );
  }

  @Cached()
  public provideLoginWithRedirectMicrosoftTeamFullAdapter(): LoginWithRedirectAdapterInteractor {
    return new LoginWithRedirectGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftTeamFull],
    );
  }

  @Cached()
  public provideLoginWithRedirectMicrosoftTeamSitesAdapter(): LoginWithRedirectAdapterInteractor {
    return new LoginWithRedirectGenericAdapterInteractor(
      this.getMicrosoftAuthUrlInteractor[ServiceAuthType.MicrosoftTeamSites],
    );
  }

  @Cached()
  public provideRefreshMicrosoftCredential(): RefreshCredentialAdapterInteractor {
    return new RefreshMicrosoftCredentialInteractor(environment.microsoft.clientId);
  }

  public provideRequiredScopesPersonalFull(): readonly string[] {
    return this.scopes[ServiceAuthType.MicrosoftPersonalFull];
  }

  public provideRequiredScopesTeamFull(): readonly string[] {
    return this.scopes[ServiceAuthType.MicrosoftTeamFull];
  }

  public provideRequiredScopesTeamSites(): readonly string[] {
    return this.scopes[ServiceAuthType.MicrosoftTeamSites];
  }
}
